<template>
  <div class="prose cecilia-container">
    <!-- Intro with animation -->
    <div
      class="intro-section noMarginBottom column alignCentreColumn animate fadeInUp"
    >
      <p class="intro-text serif smaller-text text-center largerLetterSpacing">
        THIS IS THE BEGINNING
      </p>
      <h1
        class="text-center largest-TextMag largeLetterSpacing cecilia-main-title"
      >
        CECILIA
      </h1>
      <p class="text-center italic shortWdithOnDesktop serif noMarginBottom">
        The first act and character that will turn the dream into reality and
        make us all believe.
      </p>
    </div>

    <!-- Barbara Red Image - Single image with opacity -->
    <div class="row alignCentreRow headerImgText">
      <div class="barbara-image-container animate floating">
        <img
          class="barbaraPurpleImage"
          :src="barbaraPurpleImage"
          alt="Barbara"
          style="opacity: 0.6"
        />
      </div>

      <div class="headerQuote row fullWidth alignCentreRow alignTopRow">
        <div class="fromCecilia animate fadeInRight"><h2>From Cecilia</h2></div>

        <div class="toARealGirl animate fadeInRight">
          <h2>to a<span class="serif textColour"> real girl</span></h2>
        </div>
      </div>
    </div>

    <!-- Character Page Micro-NDA -->
    <MicroNDA variant="deeper" />

    <!-- Collections with staggered animation -->
    <div
      class="hugeMarginTop bigMarginBottom collections-section animate fadeInUp"
    >
      <h3 class="text-center">The 4 Collections</h3>
      <p class="fullWidth text-center italic grey-dark">2027-2028</p>

      <SwissStyleTable
        :headers="['Season & Project', 'Type', 'Status']"
        :rows="[
          [
            ['ALONE WITH EVERYONE', 'Spring'],
            'Feature Film',
            { color: 'var(--green)', text: 'Written' },
          ],
          [
            ['DREAMSCAPES OF NO TOMORROW', 'Summer'],
            'Feature Film',
            { color: 'var(--green)', text: 'Written' },
          ],
          [
            ['THE ABSURD SOJOURN', 'Fall'],
            ['Non-fiction & Fiction', 'Hybrid TV Series'],
            { color: 'var(--grey)', text: 'In development' },
          ],
          [
            ['LITTLE DEATH / LA PETITE MORT', 'Winter'],
            ['Mockumentary', 'Immersive Exhibition & Performance'],
            { color: 'var(--grey)', text: 'In development' },
          ],
        ]"
        :statusColumnIndex="2"
        :emphasizeColumnIndex="0"
      />
    </div>

    <!-- About Cecilia with side illustration on desktop -->
    <div class="about-section">
      <h2 class="animate fadeInLeft bold about-cecilia-title">About Cecilia</h2>
      <div class="about-content">
        <div class="about-text">
          <p class="animate fadeInUp">
            Cecilia is the first manifestation of this spark of divine
            consciousness. As a mythical being that descends to earth in order
            to discover the mystery of her creation and instruct the human race,
            she carries the magic of the future crystallized in the esthetics of
            the past, the infinite vastness of what’s possible for a being born
            in the matrix in the role of a human being.
          </p>
          <p class="animate fadeInUp delay-1">
            Partly human, partly something else, Cecilia is a supernatural force
            ensouled in the form of a sculpture that eventually becomes alive by
            the love and surrender of her creator. Just like Aphrodite/Venus
            born out of the alchemical process between the ocean’s foam and her
            father’s genitals, it is the true love and surrender of her creator
            that marks the possibility of her existence but once her abstraction
            is given human definition, Cecilia awakens in a world where other
            humans seem lost and asleep, merely characters in a play that keeps
            them hostages of their deep ingrained unconscious programming,
            reinforcing the same self-abandonment and disempowerment that keeps
            them out of their sovereignty.
          </p>
          <p class="animate fadeInUp delay-2">
            At first, even though she is inherently different, Cecilia remains
            unconscious while she learns what it is to be human and move through
            the world’s expectations and cultural norms while remaining true to
            what speaks from within her. This innocence holds both the gift of
            her conception as the key to her liberation. Awakened either by
            cosmic force, love or destiny, this divine work of art starts a
            journey of remembrance, while unknown forces push her towards
            conscious choice and her ultimate destiny.
          </p>
        </div>
        <div class="about-images">
          <img
            :src="images[0]"
            alt="Cecilia Image 1"
            class="animate fadeInRight"
          />
          <img
            :src="images[1]"
            alt="Cecilia Image 2"
            class="animate fadeInRight delay-1"
          />
          <p class="animate fadeInUp delay-1">
            Cecilia will be represented by the past. We interact with Cecilia as
            if we are already nostalgic about something long gone. In a way,
            Cecilia is like an abstraction made human, never intended to really
            stay, or stay unaltered. As a multifaceted being, she is recognized
            by her beauty and vulnerability. She has a sense of innocence that
            appears layered with mystery and the undiscovered potential of what
            she really descended for. Somehow preciously intemporal in her
            physical form but with an essence that belongs to different time
            realms, she is subject to projection from other people due to her
            undefinable enigmatic nature and oceanic and other-worldly presence.
            This projection is the reason why Cecilia came and the same reason
            why she departs, morphing into Patricia.
          </p>
        </div>
      </div>
    </div>

    <!-- Stories with alternating layouts -->
    <div class="largestMarginTop stories-section">
      <h2 class="largestMarginTop text-center animate fadeInUp">
        The 4 Stories
      </h2>
      <p class="text-center animate fadeInUp">
        Cecilia's 4 seasons & films will evolve through the aesthetics of the
        30's, 40's, 50's and 60's in the history of cinema. The audience will
        observe Cecilia but Cecilia won't know she is being observed,
        establishing the basis from which this entire journey will take the
        spectator on a ride towards the realm on the other side of the screen.
      </p>

      <!-- Story 1 with right-aligned images on desktop -->
      <div class="film-title-container">
        <div class="circleNumber">1</div>
        <h3 class="film-title film-title-alone serif animate fadeInDown">
          ALONE
          <span class="wavy-word">
            <span>w</span><span>i</span><span>t</span><span>h</span>
          </span>
          EVERYONE
        </h3>
      </div>
      <section
        class="story-section story-right animate fadeIn"
        data-theme-group="cecilia"
      >
        <div class="story-content">
          <p class="animate fadeInLeft delay-1">
            The first collection is based on the feature film “Alone With
            Everyone”, where we erupt from the dichotomous fight between the
            contrasting tones of 30’s noir films and the opposing emergence of
            vivid colors in technicolor motion pictures from the 40’s. This
            motion film features both visual aesthetics in a metaphorical battle
            between light and darkness, chaos and order, creation and
            potentiality, until the two forces merge allowing a new reality to
            come into form synchronous with Cecilia’s birth.
          </p>

          <p class="animate fadeInLeft delay-1">
            Matt is an acclaimed painter that lives in the success that his
            series, inspired by the intuitive language of African tribal masks,
            has provided. When walking on a street, he is accidentally led to
            the sculpture of a woman suspended on the edge of an orgasm who
            summons him through a shop window. The sculptor says her name is
            Cecilia and, convinced that Matt has what it takes to decipher her,
            alerts him to the fact that not all dreams are up to all men.
            Already in the intimacy of his studio, the days go by with Matt
            intoxicated by the oceanic aura of his new muse and by the mysteries
            of nature she evokes. After many failed attempts in translating what
            Cecilia communicates to him to the surface of his canvas, one
            morning, she magically transitions from marble to flesh at the
            command of his touch, leaving him astonished both by the miracle and
            his inability to fully surrender to it. Days pass with frustration
            marking his routine, a picture slowly merging in the surface of his
            canvas, and as the tension grows higher and his clarity lower, Matt
            leaves the apartments eager to experience the streets of Manhattan
            where an initiatory journey through Sun Ra’s music, an african
            american spiritual ceremony and a group of strange characters lead
            him through a jigsaw of enigmas and riddles directly back to his
            apartment, where his creations await him for a descent into the
            realms of imagination. In this liminal space, he is initiated into
            his unconscious so a forking of the ways can occur.
          </p>

          <p class="animate fadeInLeft delay-1">
            The photography and sound of the film will allow a journey through
            mythical visuals, requiring rhythmic and pictorial changes, making
            each segment sensorially descriptive of Matt's dance of seduction
            with what he cannot confront about himself. Visuals define the
            temporal experience of the character through which we are introduced
            to the story. Thus, the alternating choice between technicolor and
            black and white/noir will illustrate this conflict and integration.
            Sunlight and moonlight are symbolized by the two most contrasting
            moments in the narrative. Sunlight prevails through the blind
            objectivity of the conscious ego and moonlight does so through the
            abstract language of the unconscious – but together they struggle
            for fusion on screen. The scenes that happen during the day, when
            his ego is in control of the narrative, contain all the elements of
            internal resistance materialized externally, forcing him to look at
            them in a more objective way. The scenes that happen at night or
            inside are fused in black and white/noir, with strong contrasts,
            shifting and textured shadows, which allow to express the abstract
            and misty language of the subconscious, making the protagonist's
            reality something close to fantasy, thus enhancing the need for him
            to surrender to the dream so both realities can collapse and emerge
            as one.
          </p>

          <div class="film-details animate fadeInLeft delay-2">
            <div class="detail-section">
              <h4>Keywords</h4>
              <p>
                <span>Oneiric</span>
                <span>mythical</span>
                <span>surrealistic</span>
                <span>stylistic extremes</span>
                <span>acoustic</span>
                <span>figurative</span>
                <span>theatrical</span>
                <span>dramatic</span>
                <span>imaginative</span>
                <span>character-driven</span>
                <span>intelligent</span>
                <span>layered</span>
                <span>poetic</span>
                <span>erotic</span>
                <span>contemplative</span>
                <span>touching</span>
                <span>stylized</span>
              </p>
            </div>

            <div class="detail-section">
              <h4>Themes</h4>
              <p>
                Male protagonist, painter, creator and creation relationship,
                creative process, mystic love, divine love, based on original
                novella, older man younger woman relationship, siren,
                supernatural being, transformative journey, NYC, non-linear
                time, imagination, initiation, fairy-tale, legends & myths,
                voodoo, dream realm, dangerous attraction, sculptures, tests &
                riddles.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual Style</h4>
              <p>
                Cool and light warm – pastels, pearl & beige - colored tones
                (spring-like) and contrasted noir B&W (winter-like). In terms of
                cinematography, unusual camera angles, immersive soundtrack and
                long takes with smooth camera movements amplifying the suspense,
                creating an uninterrupted, immersive experience.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual References</h4>
              <p>
                Ramble Fish, Pleasantville (for the b&w/technicolor contrast),
                Seven Year Itch, Leave Her to Heaven, La Belle Noiseuse, Space
                is the Place, Mr. Turner (for the technicolor color grading
                tones) and 8½, The Big Combo, I Walked with a Zombie 1943,
                Stranger on the Third Floor, Double Indemnity, Sunset Boulevard
                (for the black & white lighting)
              </p>
            </div>
          </div>
        </div>
        <div class="story-images">
          <div class="film1-gallery">
            <img
              v-for="(img, index) in film1Images"
              :key="index"
              :src="img"
              :alt="`Film 1 Scene ${index + 1}`"
              class="animate zoomIn"
            />
          </div>
        </div>

        <div class="film-quote-container">
          <div class="film-quote film-quote-alone animate fadeIn delay-1">
            The Woman Who Fell to Earth
          </div>
        </div>
      </section>

      <!-- Story 2 with left-aligned images on desktop -->

      <div class="film-title-container">
        <div class="circleNumber">2</div>
        <h3 class="film-title film-title-dreamscapes serif animate fadeInDown">
          Dreamscapes <br />
          <small class="baselineShiftUp">of</small> No <br />Tomorrow
        </h3>
      </div>
      <section
        class="story-section story-left animate fadeIn"
        data-theme-group="cecilia"
      >
        <div class="story-images">
          <div class="film2-gallery">
            <img
              v-for="(img, index) in film2Images"
              :key="index"
              :src="img"
              :alt="`Film 2 Scene ${index + 1}`"
              class="animate zoomIn"
            />
          </div>
        </div>
        <div class="film-quote-container">
          <div class="film-quote film-quote-dreamscapes animate fadeIn delay-1">
            Cecilia has woken up but Cecilia is still asleep. Who is Cecilia
            when others aren't looking?
          </div>
        </div>
        <div class="story-content">
          <p class="animate fadeInRight delay-1">
            The second collection is based on the feature film “Dreamscapes of
            No Tomorrow”. After the light crystallized into creation making
            Cecilia emerge from sculpture to a real girl, the second collection
            & motion film will take us through the awakening of her
            consciousness into this humanoid form.
          </p>

          <p class="animate fadeInRight delay-1">
            Visually nostalgic, imaginative and stylized, this film will be a
            hymn to the technicolor motion pictures of the 50’s, this time
            illustrating the battle between Cecilia’s scripted role as a muse
            and source of inspiration for her lover and, on the other side, her
            destiny that calls from a different realm, merged into the screen
            like a sensorial explosion of color, vibration and the depth of the
            human senses. Evocative of the awakening journey of the divine
            feminine, this film takes us into the inner world of a woman and the
            unique depth of her sensibility in a reality that seems dominated by
            manufactured systems, structures and outer authorities. While
            Cecilia evolves to the rhythm of the physical senses and different
            colors, she tries to learn the history of the race she is now a part
            of, growing to understand how and why she is different from those
            she lives with.
          </p>

          <p class="animate fadeInRight delay-1">
            Through the power of the physical senses and the influence of the
            color palette, Cecilia starts to sprout like the improbability of
            Matt’s paintings into a distinctive nature that seems coordinated
            with the force of his passionate creativity. While this growth
            occurs during the day, witnessed by Matt and his ever-ready pencil,
            in the solitude of her oceanic presence something different starts
            making its way towards the surface. Visited by beings from other
            realms Cecilia starts establishing a connection with the unseen,
            having small glimpses of other timelines appear in her already
            stablished narrative to show her a different route to her living
            existence. Like in old sci-fi films, the visions add a tone of
            surrealism to her already fragmented perception, allowing the unseen
            to pierce the seen reality through alive orbs of colors and the
            subsequent awakening of her metaphysical senses.
          </p>

          <p class="animate fadeInRight delay-1">
            While all of this is happening, Matt’s need for Cecilia’s
            inspiration grows higher as his inability to fully comprehend what
            she is going through does too. While he learns to surrender to the
            unpredictability of this divine love, she learns human concepts on
            time, destiny, past and future, eager to understand why she became
            alive in the first place. As the unfamiliar appears in the context
            of the familiar, the unknown is no longer symbolized by the
            contrasted black and white but by the cosmos itself and everything
            outer-space that infects the protagonist’s inner world with
            futuristic imagery from other timelines and realms that she seems to
            be forgetting as her humanity gets anchored in the body of this
            woman that fell to earth.
          </p>

          <p class="animate fadeInRight delay-1">
            The photography and sound of the film will create a deeply sensorial
            experience where raw unprocessed stimulus will help us immerse with
            the life that is vigorously awakening in Cecilia, moving from the
            collection of visual and sensory signals, to a serendipitous finding
            and the final translation of those received stimulus into action
            potentials that awaken the powers of imagination. The use of color
            in lighting and the costume design will help achieve a monochrome
            tone, enhancing specific emotions and moods and also highlighting
            textures, shapes, lines and the visual impact of each growth phase.
            For the comic add-ons a mixture of optical special effects with
            matte paintings an animation will create a mergence between the 3D
            world and the other quantum dimensions, taking us into the
            protagonist’s unique world, while honoring the special effects of
            50’s sci fi films. The story evolves as a slow drowning in the
            infinity that exists within Cecilia so when the involution becomes
            evolution and the climax arrives it dictates how time will stretch
            from this point on in relation to her courage, taking both
            characters on a new phase and stage of their self-discovery,
            departing from New York towards Europe for a self-proclaimed
            destiny.
          </p>

          <div class="bigMarginTop film-details animate fadeInRight delay-2">
            <div class="detail-section">
              <h4>Keywords</h4>
              <p>
                <span>Oneiric</span>
                <span>enigmatic</span>
                <span>stylized</span>
                <span>utopian</span>
                <span>beautifully shot</span>
                <span>exciting</span>
                <span>sensory</span>
                <span>narrative</span>
                <span>graphic</span>
                <span>character driven</span>
                <span>visual</span>
                <span>evocative</span>
                <span>emotionally resonant</span>
                <span>absorbing</span>
                <span>psychological</span>
                <span>captivating</span>
                <span>suspenseful</span>
              </p>
            </div>

            <div class="detail-section">
              <h4>Themes</h4>
              <p>
                Female protagonist, surrealism, love, slow awakening, inner
                world, nonlinear timeline, dream realm, self-discovery, fashion
                & aesthetics, belonging, confinement, muse, purpose, human
                history, the past & the future.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual Style</h4>
              <p>
                Warm, vivid & vibrant coral colored tones (summer-like) with
                comic add ups, optical special effects with matte painting and
                animation (interdimensional futuristic like) blending known and
                unknown elements in the screen, creating contrast between what
                everyone sees under day sunlight and the magical encounters that
                only Cecilia sees where she is visited by voices and visions of
                those who walk with her from different realms. The photography
                will follow the three-strip technicolor look, with vivid and
                saturated colors.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual References</h4>
              <p>
                Time Machine 1960, Vertigo, Gentleman Prefer Blondes, The
                Umbrellas of Cherbourg, Fantastic Voyage, Suspiria 1977, All
                that Heaven Allows, Thief of Bagdad, Breakfast at Tiffany's (3
                strip technicolor) and Forbidden Planet 1956, Who Framed Roger
                Rabbit, Bell Book & Candle, Star Wars (for the comic add ups)
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Story 3 with right-aligned images on desktop -->

      <div class="film-title-container">
        <div class="circleNumber">3</div>
        <h3 class="film-title film-title-sojourn serif animate fadeInDown">
          The <span class="red">Absurd</span> Sojourn
        </h3>
      </div>
      <section
        class="story-section story-left animate fadeIn"
        data-theme-group="cecilia"
      >
        <div class="story-content">
          <p class="animate fadeInLeft delay-1">
            The third collection is based on the mini tv series “The Absurd
            Sojourn”, where fiction & non-fiction mix to create a precedent
            format in this brand’s narrative style that will be explored with
            more depth & complexity in later collections. In order to achieve
            this, the actors will be put in a pre-selected set and both through
            their in-character genius and a pre-partial defined script they will
            be guided towards the story.
          </p>

          <p class="animate fadeInLeft delay-1">
            The narrative arch of this limited tv series follows the lead of the
            last motion film, after Cecilia & Matt depart on a journey towards
            Europe, a new chapter of their story begins, leading to an
            inevitable change that defies the status quo. While expanding
            through new horizons, their reality starts collapsing, changing like
            the leaves in autumn, moving from the coral tones of the urban New
            York life to the deep earthy richness of rural and coastal France,
            Italy & Greece. The 7 episodes will be set in those 3 locations, in
            a sort of long roadtrip, where the couple decides to spend some time
            abroad so Cecilia can discover the world beyond New York. What is at
            first a need for exploration and discovery soon becomes an act of
            desintegration. The couple decides to stay for a period of one month
            in each location, where the cultural influence, the surrounding
            people and their daily routines awaken new perspectives.
          </p>

          <p class="animate fadeInLeft delay-1">
            Cecilia starts to experiment with what is like to be a multifaceted
            being who is not really sure of who she truly is beneath the form
            she seems to have awakened into. As her awareness begins to fork
            between the story that wants to emerge from within her and the one
            that this human life tried to define her with, it pushes her to
            experiment with personas, masks and roles and to follow the creative
            pulse that wants to claim its place. While her inner nature fights
            towards liberation and she looks for answers for her own life,
            Matt’s work and life take a new turn too, unlocking a new way of
            creating and understanding the nature of reality as it is and the
            role that his free will plays in it. Matt starts experimenting with
            other types of mixed media art that symbolize the synergy taking
            place both on screen and in their connection but as Matt stretches
            and blends the potentiality of what his creation can become, Cecilia
            resists this influence by searching for the sovereign woman in her.
          </p>

          <p class="animate fadeInLeft delay-1">
            While she stands in rooms full of mirrors, other people’s faces look
            at her, trying to awaken the dormant facets within. She reclaims her
            story through passion and creativity and by rejecting the
            established language and finally allowing her nature to grow and the
            story to shift, the narrative offers her a way out. This is the
            first – yet subtle – emergence of the real girl beyond Cecilia,
            fighting for her way in a world dominated by masks.
          </p>

          <p class="animate fadeInLeft delay-1">
            Lead by both the female and male protagonists, in the course of
            these 7 episodes their viewpoints blend and alchemize, come together
            and fall apart, so their need for differentiation pushes them
            towards what’s true behind the illusions and constraints of social
            expectations. What once united them now seems to be the reason why
            they depart but it is also by separating that they are able to
            unite, their relationship becoming a receptacle for the more
            meaningful transformation taking place. While Cecilia finally finds
            the potential waiting to be birthed into form and Matt understands
            the reason behind this sojourn, the film closes with the
            announcement that changes the course of their life and the
            audience’s experience. Once again, the couple departs from Europe
            back home to New York city, where Cecilia’s last act and her little
            death will give room to a bigger woman.
          </p>

          <p class="animate fadeInLeft delay-1">
            Absurdly satirical and vividly poetic, the photography and sound of
            this tv series will follow the aesthetics of the french nouvelle
            vague films, each episode will be visually pleasing like a moving
            painting but filled with deeply claustrophobic and surreal messages
            that communicate in between the lines of the screen’s realm,
            creating a new radical style of communication between the lovers who
            seem to be parting onto different ways to blend different aspects of
            their complex identities.
          </p>

          <div class="film-details animate fadeInLeft delay-2">
            <div class="detail-section">
              <h4>Keywords</h4>
              <p>
                <span>Absurd</span>
                <span>experimentalist</span>
                <span>spontaneous</span>
                <span>improvisational</span>
                <span>inspirational</span>
                <span>melancholic</span>
                <span>transient</span>
                <span>humorous</span>
                <span>daring</span>
                <span>offbeat</span>
                <span>avant-garde</span>
                <span>adventurous</span>
                <span>artistic</span>
                <span>satirical</span>
              </p>
            </div>

            <div class="detail-section">
              <h4>Themes</h4>
              <p>
                Lead by both female & male protagonists, nouvelle vague,
                identity, self-discovery, self-expression, companionship,
                foreign travel, exploration, sexuality, transience, redefining
                freedom, coming to age, woman's liberation, ownership, extremes,
                existentialism, creative expression, finding unique voice,
                breakup, parting ways.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual Style</h4>
              <p>
                Warm, earthy rich colored tones (autumn-like) in a 60's new age
                french cinema aesthetic. Handheld cameras providing more
                personal and spontaneous shots, with a documentary style and
                authentic intimacy. Fragmented shots with long takes.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual References</h4>
              <p>
                Le Mepris, Pierrot Le Fou, Week-End 1967, La Collectionneuse, A
                Girl is a Land, Eden and After, Hiroshima Mon Amour, A Woman is
                a woman, La Chinoise (nouvelle vague aesthetics and cuts),
                Stealing Beauty, Call me by your Name, La Piscine 1969 (for the
                immersive experience on land)
              </p>
            </div>
          </div>
        </div>
        <div class="story-images">
          <div class="film3-gallery">
            <img
              v-for="(img, index) in film3Images"
              :key="index"
              :src="img"
              :alt="`Film 3 Scene ${index + 1}`"
              class="animate zoomIn"
            />
          </div>
        </div>
        <div class="film-quote-container">
          <div class="film-quote film-quote-sojourn animate fadeIn delay-1">
            The order in the chaos
          </div>
        </div>
      </section>

      <!-- Story 4 with left-aligned images on desktop -->
      <div class="film-title-container">
        <div class="circleNumber">4</div>
        <h3 class="film-title film-title-lapetitemort serif animate fadeInDown">
          Little Death/La Petite Mort
        </h3>
      </div>
      <section
        class="story-section story-right animate fadeIn"
        data-theme-group="cecilia"
      >
        <div class="story-content">
          <p class="animate fadeInRight delay-1">
            The fourth and last collection is based on the fictionalized
            installation & performance “La Petite Mort” or “Little Death” filmed
            and edited into a mockumentary format that takes us into the
            preparation, premiere and immersive experience of Cecilia’s last
            act, her public death.
          </p>

          <p class="animate fadeInRight delay-1">
            Following the lead of the 3rd collection and the previous limited
            series, after Cecilia’s experimentations with masks, identities and
            personas, she finallly finds her voice as a storyteller. The finding
            and the need to change in order to integrate it, make her announce
            that she will sacrifice herself in order to be born anew as a
            creator that remembers nothing but only her divine destiny and her
            inherent pulse to create. Back in NYC, she organizes an exhibition
            that will take the audience – Matt’s and her group of acquaintances
            together with unknown guests - into an immersive installation, where
            Cecilia becomes a vast space for others to wander in. The
            museum/gallery is composed of different rooms that represent
            Cecilia’s different senses, thoughts and discoveries. In each room,
            the space is decorated in order to take the spectator inside
            Cecilia’s mind, spirit, sensitivity and emotionality, making each
            visitor a roaming follower in the infinity of her being. The journey
            flows through a highly sensorial experience that invites us to
            wonder, feel and release until the enigmatic climax closes the
            experience, where Cecilia’s little death takes place in the heart of
            the gallery, the open space at the center, where her body lies
            asleep in a bed of blooming flowers, like a bud breaking through the
            soil, ready for the next character to come and depart towards the
            world. Around her ankle a red thread connects her with what is
            hiding in the mist around, where guests are invited to wander in
            order to observe her unconscious body, giving her disembodied spirit
            a chance to also observe those who might be a part of her future
            reality. From the other end of the thread, in the dark and
            mysterious places of the gallery’s dark container, a stranger
            appears to awaken Cecilia’s body into what seems to have always been
            the next inevitable stage of her evolution: Patricia.
          </p>

          <p class="animate fadeInRight delay-1">
            The first creation and the last of Cecilia’s words, La Petite Mort
            was prophesied by Matt in the second feature film, making the
            gallery where the exhibition takes place a sort of cosmic womb where
            alchemy is happening. From inside the container, each member of the
            audience will act as a participant in a story that is no longer
            solely hers, giving something back to the experience intended to
            bring us towards the next step of this consciousness evolution: the
            awareness that the observed is being seen and depending on the
            observer’s projections the observed gets to choose how to respond
            and what to create. It is from this ground that Patricia will rise
            and be an artist, answering the call of her audience.
          </p>

          <p class="animate fadeInRight delay-1">
            The photography and sound of this film will illustrate the profound
            transition experienced in the cultural values of the late 60’s
            towards the 70’s. On one end, the preparing of this installation,
            Cecilia’s emotional processing in relation to it, Matt’s presence
            and his powerlessness towards her choice, the farewell of a story to
            be able to receive other - and on the other end - the immersive
            experience that each visitor will have inside the work of art upon
            its completion that will allow us to transition from the decision
            stage to the evasion one, as if Cecilia boils away like evaporated
            water. In terms of photography, the high contrasts, cold tones and
            eerie atmosphere will be a key transition from the remains of warm
            tones that are still present when Cecilia is emoting and planning
            her departure, putting us into the full realms of winter. In terms
            of sound, the mixing will be highly immersive and detailed,
            providing an intense experience into Cecilia’s breath and inner
            thoughts. The experience of this film as a whole will be specially
            designed as a multi-sensory cinematic experience like the 4dx system
            so spectators feel they are both in the exhibition and outside of it
            looking in.
          </p>

          <p class="animate fadeInRight delay-1">
            The unpredictability experienced in films emerging from the 60’s
            marks the same unpredictability of this closure for Cecilia’s story.
            Just like a story filmed on highly flammable nitrate film stock,
            Cecilia too is fragile, volatile, eager to break. She is finite but
            she is still near indestructible. Her readiness to self-sacrifice
            one story in order to create another is what gives force to this
            creative act that simultaneously end her life and begin Patricia’s
            existence and mission.
          </p>
          <div class="film-details animate fadeInRight delay-2">
            <div class="detail-section">
              <h4>Keywords</h4>
              <p>
                <span>Surrealistic</span>
                <span>psychedelic</span>
                <span>authentic & abstract</span>
                <span>literal & metaphorical</span>
                <span>eery</span>
                <span>raw</span>
                <span>technological</span>
                <span>futuristic</span>
                <span>thought provoking</span>
                <span>layered</span>
                <span>highly original</span>
                <span>honest</span>
                <span>visionary</span>
                <span>provoking</span>
                <span>intense</span>
                <span>ghostly</span>
              </p>
            </div>

            <div class="detail-section">
              <h4>Themes</h4>
              <p>
                Mockumentary, female independence & freedom, physical
                transformation, immersive exhibition, death and resurrection,
                chameleon, newsreel footage, self-sacrifice, elevation, human
                spirit, life & death, rebellion, personality swapping, identity
                swapping, life changing experience.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual Style</h4>
              <p>
                Cool & contrasted tones, low icy lights, dark deep backgrounds,
                ghost-like (winter-like) in a liminal hybrid aesthetic. The
                footage will feel immersive, personal, both taking us in close
                up shots of details and out into the big picture of what’s
                happening in the performance and through the making of it.
              </p>
            </div>

            <div class="detail-section">
              <h4>Visual References</h4>
              <p>
                Dark City, The Decalogue, Stereo 1969, Blade Runner, THX 1138,
                and real immersive exhibitions that utilize large-scale
                projections, interactive installations, and multimedia elements
                to create a truly immersive and engaging atmosphere.
              </p>
            </div>
          </div>
        </div>

        <div class="story-images">
          <div class="film4-gallery">
            <img
              v-for="(img, index) in film4Images"
              :key="index"
              :src="img"
              :alt="`Film 4 Scene ${index + 1}`"
              class="animate zoomIn"
            />
          </div>
        </div>
        <div class="film-quote-container">
          <div
            class="film-quote film-quote-lapetitemort animate fadeIn delay-1"
          >
            From Cecilia to a real girl
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted } from "vue";
import SwissStyleTable from "@/components/GenericComponents/SwissStyleTable.vue";
import useDynamicBackground from "@/composables/useDynamicBackground";
import MicroNDA from "@/components/GenericComponents/MicroNDA.vue";

export default defineComponent({
  name: "CeciliaLongread",
  components: {
    SwissStyleTable,
    MicroNDA,
  },
  setup() {
    const poster = require("@/assets/images/Cecilia/cecilia.png");
    const barbaraRedImage = require("@/assets/images/barbara_3_red.png");
    const barbaraPurpleImage = require("@/assets/images/barbara_3_purple.png");

    // Film 1 images from ceciliafilme1 folder
    const film1Images = [
      require("@/assets/images/Cecilia/ceciliafilme1/1.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/2.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/3.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/4.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/5.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/6.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/7.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/8.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/9.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/10.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/11.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/12.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/13.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme1/14.jpg"),
    ];

    // Film 2 images from ceciliafilme2 folder
    const film2Images = [
      require("@/assets/images/Cecilia/ceciliafilme2/1.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/2.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/3.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/4.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/5.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/6.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/7.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/8.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/9.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/10.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/11.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/12.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/13.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/14.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/15.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme2/16.jpg"),
    ];

    // Film 3 images from ceciliafilme3 folder
    const film3Images = [
      require("@/assets/images/Cecilia/ceciliafilme3/1.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/2.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/3.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/4.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/5.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/6.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/7.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/8.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/9.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/10.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/11.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/12.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/13.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/14.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme3/15.jpg"),
    ];

    // Film 4 images from ceciliafilme4 folder
    const film4Images = [
      require("@/assets/images/Cecilia/ceciliafilme4/0.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/1.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/2.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/3.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/4.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/5.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/6.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/7.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/8.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/9.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/10.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/11.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/12.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/13.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/14.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/15.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/16.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/17.jpg"),
      require("@/assets/images/Cecilia/ceciliafilme4/18.jpg"),
    ];

    const images = [
      require("@/assets/images/Cecilia/cecilia.png"),
      require("@/assets/images/Cecilia/cecilia_2.jpg"),
      film1Images[0], // Using ceciliafilme1 images for the first story
      film1Images[1],
      require("@/assets/images/Cecilia/cecilia_5.jpg"),
      require("@/assets/images/Cecilia/cecilia_6.jpg"),
      require("@/assets/images/Cecilia/cecilia_7.jpg"),
      require("@/assets/images/Cecilia/cecilia_8.png"),
      require("@/assets/images/Cecilia/cecilia_9.jpg"),
      require("@/assets/images/Cecilia/cecilia_10.jpg"),
    ];

    // Use the dynamic background composable
    const { currentVisibleSection, currentBodyClasses } =
      useDynamicBackground();

    onMounted(() => {
      // Animation handling with intersection observer
      const elements = document.querySelectorAll(".animate");
      const observerOptions = {
        root: null,
        rootMargin: "0px 0px 0px 0px",
        threshold: 0.01,
      };

      const handleIntersection = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          } else {
            // Remove 'active' class when it leaves viewport (for animation reversion when scrolling up)
            entry.target.classList.remove("active");
          }
        });
      };

      const observer = new IntersectionObserver(
        handleIntersection,
        observerOptions
      );

      elements.forEach((el) => {
        if (el) observer.observe(el);
      });

      // Listen for theme changes globally
      window.addEventListener("themeChange", (event) => {

        // Find and update any drawer elements
        setTimeout(() => {
          // Use a timeout to ensure the drawer is rendered
          const drawer = document.querySelector(".BottomDrawer");
          if (drawer) {
            if (event.detail.isLightTheme) {
              drawer.classList.add("light-theme");
            } else {
              drawer.classList.remove("light-theme");
            }
          } else {
          }

          // Notify any drawers that mount later
          const drawerElements = document.querySelectorAll(
            "[class*='drawer'], [class*='Drawer']"
          );
          drawerElements.forEach((drawerEl) => {
            if (event.detail.isLightTheme) {
              drawerEl.classList.add("light-theme");
            } else {
              drawerEl.classList.remove("light-theme");
            }
          });
        }, 100);
      });

      // Notify when this component mounts a drawer
      const drawerElement = document.querySelector(".BottomDrawer");
      if (drawerElement) {
        window.dispatchEvent(
          new CustomEvent("drawerMounted", {
            detail: { drawerElement },
          })
        );
      }
    });

    return {
      poster,
      barbaraPurpleImage,
      images,
      film1Images,
      film2Images,
      film3Images,
      film4Images,
      currentVisibleSection,
      currentBodyClasses,
    };
  },
});
</script>

<style scoped>
/* Base styles */

.film-title {
  width: 100%;
  margin: var(--space) 0 var(--doubleSpace) 0;
  text-align: center;
  font-size: calc(var(--largerFontSize) * 3.5) !important; /* Increased size */
}

/* Film title styling */

.film-title-alone {
  font-family: "AlternateGotNo2D", sans-serif;
  letter-spacing: 0.07em;
  color: #e7cf5d;
}
.film-title-alone .wavy-word {
  font-size: calc(var(--largerFontSize) * 2.6) !important; /* Increased size */

  text-transform: none !important; /* keep it lowercase              */
  letter-spacing: 0; /* tighten spacing (optional)     */
}

.wavy-word span {
  /* letters behave like blocks     */
  display: inline-block;
  letter-spacing: 0.09em;
}

/* odd letters up, even letters down – adjust the 0.15em to taste */
.wavy-word span:nth-child(odd) {
  vertical-align: 0.1em; /* raise                          */
}
.wavy-word span:nth-child(even) {
  vertical-align: 0.2em; /* drop                           */
}
.wavy-word span:last-child {
  vertical-align: 0.35m; /* raise                          */
}

.film-title-dreamscapes {
  font-family: "Core Sans", sans-serif;
  color: var(--cecilia_dreamscapes-color-accent-color);
  font-weight: 900;
  font-size: calc(var(--largerFontSize) * 3) !important;
  opacity: 0;
  transform: scale(0.75) rotate(2deg);
  animation: dreamscapesEntrance 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.2s;
  text-shadow: 5px 5px var(--cecilia_dreamscapes-color-counter-color);
  -webkit-text-stroke: 3px var(--cecilia_dreamscapes-color-counter-color);
}

.baselineShiftUp {
  display: inline-block;
  transform: translateY(-0.4em);
}

.film-title-sojourn {
  /* The Absurd Sojourn */
  font-family: "Plinc Interlock", sans-serif;
  text-transform: uppercase;
}

.film-title-lapetitemort {
  /* Little Death/La Petite Mort */
  font-family: "Biro Script Sloppy", sans-serif;
  font-size: calc(var(--largerFontSize) * 2) !important; /* Increased size */
}

.cecilia-container {
  max-width: 900px;
  margin: 0 auto;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: var(--space) 0;
  width: 100%;
}

.headerImgText {
  max-height: 260px;
}

.headerQuote {
  width: 100%;
  align-items: flex-start !important;
  position: relative;
  height: 110px;
}
.fromCecilia {
  position: absolute;
  top: 0;
  left: 0;
  white-space: nowrap;
}
.toARealGirl {
  position: absolute;
  white-space: nowrap;
  bottom: 0;
}

/* Global theming */
body {
  transition: background-color 0.5s ease-in-out, color 0.5s ease-in-out;
  background: var(--bg-main-dark);
  color: var(--text-white);
}

body.light-theme {
  background-color: var(--bg-light) !important;
  color: var(--text-light) !important;
}

/* Light theme text adjustments */
body.light-theme .cecilia-container {
  color: var(--text-light);
}

body.light-theme .story-section * {
  color: var(--text-light);
}

/* Add transitions to the drawer background */
.BottomDrawer {
  transition: background-color 0.5s ease, color 0.5s ease;
  background-color: var(--bg-main-dark);
  color: var(--text-white);
}

/* Style for light-theme drawer */
.BottomDrawer.light-theme {
  background-color: var(--bg-light) !important;
  color: var(--text-light) !important;
}

/* Gradient separators removed */

.intro-text {
  font-size: var(--smallFontSize);
  line-height: 1.4;
  margin-bottom: calc(var(--space) * 6.5);
}
.intro-text::after {
  content: "";
  position: absolute;
  top: calc(var(--regularFontSize) * 2);
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 1px;
  background: var(--text-white);
}

.largest-TextMag {
  font-size: calc(var(--giganticFontSize) * 1.2) !important;
  font-weight: 630;
}

img {
  max-width: 100%;
  height: auto;
  border-radius: var(--border-radius-medium);
}

.maxFullHeight {
  max-height: 60vh;
  width: auto;
}

/* Section styling */
.intro-section,
.collections-section,
.about-section,
.stories-section {
  margin-bottom: var(--doubleSpace);
}

/* Barbara Red Image treatment */
.barbara-image-container {
  position: relative;
  width: 100%;
  height: 60vh;
  max-height: 400px;
  margin: var(--doubleSpace) auto;
  overflow: visible;
}

.barbaraPurpleImage {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: auto;
  border-radius: var(--border-radius-medium);
  transition: opacity 0.3s ease;
}

/* Big quote styling */
.big-quote {
  position: relative;
  font-size: 1.8rem;
  line-height: 1.3;
  margin: var(--doubleSpace) 0;
  padding: var(--space);
  text-align: right;
  max-width: 90%;
  margin-left: auto;
  border-left: none;
  transform: translateX(100%);
  opacity: 0;
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease;
}

.big-quote.active {
  transform: translateX(0);
  opacity: 1;
}

.big-quote::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 5px;
  background: var(--dark);
}

@media (min-width: 768px) {
  .big-quote {
    font-size: 2.4rem;
    max-width: 80%;
    padding-right: 0;
    margin-right: -5vw;
  }
}

/* About section layout */
.about-content {
  display: flex;
  flex-direction: column;
  gap: var(--space);
}

.about-text {
  flex: 1;
}

.about-images {
  display: flex;
  flex-direction: column;
  gap: var(--space);
}

/* Stories section */
.story-section {
  margin-bottom: var(--doubleSpace);
  display: flex;
  flex-direction: column;
  gap: var(--space);
  max-width: 100%;
  box-sizing: border-box;
}

.story-content {
  flex: 1;
  gap: var(--largeSpace);
}

.story-content h3 {
  margin-bottom: var(--space);
  font-size: var(--biggerFontSize);
}

.story-images {
  display: flex;
  flex-direction: column;
  gap: var(--space);
}

.film1-gallery {
  display: flex;
  flex-direction: column;
  gap: var(--space);
  margin-top: var(--space);
}

.film1-gallery img,
.film2-gallery img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
}

.film2-gallery,
.film3-gallery,
.film4-gallery {
  display: flex;
  flex-direction: column;
  gap: calc(var(--space) / 1); /* Reduced gap for all film galleries 2-4 */
  margin-top: var(--space);
}

.film3-gallery img,
.film4-gallery img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
}

/* Explicitly apply transition to animated images within galleries */
.film1-gallery img.animate,
.film2-gallery img.animate,
.film3-gallery img.animate,
.film4-gallery img.animate {
  transition: opacity 0.25s ease, transform 0.25s ease;
  opacity: 0;
  transform: scale(0.95);
}
.film1-gallery img.animate.active,
.film2-gallery img.animate.active,
.film3-gallery img.animate.active,
.film4-gallery img.animate.active {
  opacity: 1;
  transform: scale(1);
}

/* Film title container styling */
.film-title-container {
  width: 100%;
  max-width: 900px;
  margin: calc(var(--doubleSpace) * 4) auto var(--doubleSpace) auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* Film quote container styling */
.film-quote-container {
  width: 100%;
  margin: var(--doubleSpace) auto;
  text-align: center;
  grid-column: 1 / -1;
  box-sizing: border-box;
}

/* Alternatively, you could target each specific quote class individually */
.film-quote-alone,
.film-quote-sojourn,
.film-quote-lapetitemort {
  font-size: calc(var(--regularFontSize) * 3.5); /* Increased font size */
}

/* Keep the original size for film-quote-dreamscapes */
.film-quote-dreamscapes {
  font-size: calc(var(--regularFontSize) * 2); /* Original size */
}
/* Film quote styling */
.film-quote {
  margin: 0 auto;
  padding: var(--space);
  position: relative;
  border-left: none;

  padding: var(--doubleSpace) var(--space);
  width: 100%;
  box-sizing: border-box;
}

.film-quote-alone {
  font-family: "AlternateGotNo2D", sans-serif;
  /* color: #e7cf5d; */
}

.film-quote-dreamscapes {
  font-family: "Core Sans", sans-serif;
  font-weight: 900;
}

.film-quote-sojourn {
  font-family: "Plinc Interlock", sans-serif;
}

.film-quote-lapetitemort {
  font-family: "Biro Script Sloppy", sans-serif;
  color: var(--primary);
  font-size: calc(var(--regularFontSize) * 3);
}

.detail-section {
  margin-bottom: var(--space);
  margin-top: var(--bigSpace);
}

.detail-section h4 {
  font-size: var(--smallFontSize);
  color: var(--text-white);
  margin-bottom: var(--smallSpace);
  text-transform: uppercase;
  letter-spacing: 0.25em;
  font-weight: 250;
  opacity: 1;
}

.detail-section p {
  font-size: var(--smallFontSize);
  color: var(--grey-lighter);
  line-height: 1.6;
}

/* Pill styles for keywords */
.detail-section p {
  display: flex;
  flex-wrap: wrap;
  gap: var(--smallSpace);
}

.detail-section:first-child p {
  display: flex;
  flex-wrap: wrap;
  gap: var(--smallSpace);
}

.detail-section:first-child p span {
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--text-white);
  padding: 2px 8px;
  border: 1px solid var(--text-white);
  border-radius: var(--border-radius-pill);
  font-size: calc(var(--smallFontSize) * 0.9);
  font-weight: 220;
  margin-bottom: var(--smallSpace);
}

body.light-theme .detail-section:first-child p span {
  color: var(--text-light);
  border-color: var(--text-light);
}

.two-column-content {
  display: flex;
  flex-direction: column;
  gap: var(--space);
}

table {
  margin-bottom: 110px;
}

@media (min-width: 768px) {
  .story-section .film-quote-container {
    grid-column: 1 / -1;
    text-align: center !important;
    margin: var(--doubleSpace) auto;
    width: 100%;
    max-width: 900px;
  }

  /* Desktop-specific styles */
  .cecilia-container {
    padding: 0 var(--largeSpace);
    max-width: 900px;
    overflow: hidden;
    box-sizing: border-box;
  }

  .about-content {
    flex-direction: row;
    align-items: flex-start;
  }

  .about-images {
    width: 40%;
  }

  .story-section {
    display: grid;
    grid-template-columns: 62% 34%;
    align-items: flex-start;
    gap: var(--doubleSpace);
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    justify-content: space-between;
    box-sizing: border-box;
  }

  .story-images {
    width: 100%;
    align-self: flex-start;
    position: static;
    transform: none;
  }

  .story-section.story-right {
    grid-template-areas: "content images";
  }

  .story-section.story-left {
    grid-template-areas: "images content";
    grid-template-columns: 34% 62%; /* Wider text column for film 2 and 4 */
  }

  .story-section.story-right .story-content {
    grid-area: content;
  }

  .story-section.story-right .story-images {
    grid-area: images;
  }

  .story-section.story-left .story-content {
    grid-area: content;
  }

  .story-section.story-left .story-images {
    grid-area: images;
  }

  /* Film backgrounds for story 2 and 4 */

  .story-content {
    width: 100%;
    max-width: 100%;
  }

  /* Make fullWidth-title contained within max-width */
  .fullWidth-title {
    width: 100% !important;
    max-width: 900px !important;
    margin: var(--doubleSpace) auto !important;
    padding-left: var(--space);
    padding-right: var(--space);
    position: relative;
    box-sizing: border-box;
    grid-column: 1 / -1; /* Make it span all columns in grid layouts */
  }

  /* Two-column layout styling for "The order in the chaos" instead of three columns */
  .two-column-content {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: var(--doubleSpace);
    width: 100%;
  }

  .two-column-content .story-content {
    width: 55%;
    max-width: 55%;
  }

  .two-column-content .story-images {
    width: 40%;
    position: sticky;
    top: var(--space);
  }
}

@media (max-width: 767px) {
  .largest-TextMag {
    font-size: calc(var(--giganticFontSize) * 0.9) !important;
  }

  .headerQuote h2 {
    font-size: var(--largerFontSize) !important;
  }

  .barbara-image-container {
    height: 30vh;
    max-height: 200px;
  }

  /* On mobile, ensure text comes before images in all story sections */
  .story-section.story-left {
    display: flex;
    flex-direction: column;
  }

  .story-section.story-left .story-content {
    order: 1;
  }

  .story-section.story-left .story-images {
    order: 2;
  }

  .story-section > .film-quote-container {
    order: 3;
  }

  /* Film title styling */
  .film-title-container {
    margin: calc(var(--doubleSpace) * 3) auto var(--largeSpace) auto;
    text-align: center;
  }
  .film-title {
    margin: var(--space) 0 var(--space) 0;
    font-size: calc(
      var(--largerFontSize) * 1.9
    ) !important; /* Increased size */
  }

  .film-title-alone {
    font-family: "AlternateGotNo2D", sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .film-title-alone .wavy-word {
    font-size: calc(
      var(--largerFontSize) * 1.3
    ) !important; /* Increased size */

    text-transform: none !important; /* keep it lowercase              */
    letter-spacing: 0; /* tighten spacing (optional)     */
  }

  .film-title-dreamscapes {
    font-size: calc(
      var(--largerFontSize) * 1.6
    ) !important; /* Ensure 'Flash' is the correct font-family name from its stylesheet */
    text-shadow: 4px 4px var(--cecilia_dreamscapes-color-counter-color);
    -webkit-text-stroke: 2px var(--cecilia_dreamscapes-color-counter-color);
  }

  .film-title-sojourn {
    /* The Absurd Sojourn */
    font-family: "Plinc Interlock", sans-serif;
    text-transform: uppercase;
  }

  .film-title-lapetitemort {
    /* Little Death/La Petite Mort */
    font-family: "Biro Script Sloppy", sans-serif;
    font-size: calc(var(--largerFontSize) * 1) !important; /* Increased size */
  }

  .film-quote-lapetitemort {
    font-size: calc(var(--regularFontSize) * 2.5);
  }
  /* Keep animations within visible boundaries on mobile */
}
</style>
